import './App.css';

import { RefCurrentPage } from './hooks/NavigationHooks';
import * as locCon from './LocalConstants';
import * as libCon from './community-hats-js-library/Constants.js';

import DashboardSelect from './pages/DashboardSelect';
import DashboardExpiryParticipants from './pages/DashboardExpiryParticipants';
import { useEffect, useRef, useState } from 'react';

import { Button, Typography, Input, Space } from 'antd';
import DashboardDeploymentProgress from './pages/DashboardDeploymentProgress';
import DashboardDeploymentStatus from './pages/DashboardDeploymentStatus';
import DashboardDataStreamQuality from './pages/DashboardDataStreamQuality';
import DashboardCollectionStatus from './pages/DashboardCollectionStatus';
import DashboardSingleCollectionStatus from './pages/DashboardSingleCollectionStatus';
import { RefIsLoggedIn } from './hooks/AuthenticationHooks';
import { setStoredValue } from './hooks/GeneralStoreHooks';
import { formatISO } from 'date-fns';
import { isNullOrUndefined } from './community-hats-js-library/utils/generalFunctions.js';
import Header from './components/Header';
import DashboardUploadLabResults from './pages/DashboardUploadLabResults';
import DashboardNameMatcher from './pages/DashboardNameMatcher.js';
import DashboardGlobalCollectionStatus from './pages/DashboardGlobalCollectionStatus.js';
import { refreshAllDatasets } from './hooks/DatasetHooks.js';
import { clearApiToken, getApiToken } from './utils/apiTokenFunctions';
import DashboardPriorityParticipantVisit from './pages/DashboardPriorityParticipantVisit.js';
import DashboardGoveeActivationCodes from './pages/DashboardGoveeActivationCodes.js';
import DashboardExhibitionSelect from './pages/DashboardExhibitionSelect.js';
import DashboardExhibitionFlippingCards from './pages/DashboardExhibitionFlippingCards.js';
import DashboardActionItems from './pages/DashboardActionItems.js';
import ParticipantUnenroll from './pages/ParticipantUnenroll.js';
import DashboardParticipantsToBeUnenrolled from './pages/DashboardParticipantsToBeUnenrolled.js';
import DashboardDocuments from './pages/DashboardDocuments.js';
import { RefContextLanguage } from './providers/LanguageProvider';

const { Text } = Typography;

function App() {
  const i18n = RefContextLanguage();

  // Navigation
  const currentPage = RefCurrentPage();

  // Password
  const [showMessage, setShowMessage] = useState(false);
  const [password, setPassword] = useState(null);

  // Authentication
  const isLoggedIn = RefIsLoggedIn();

  // Every dataset call now goes through the proxy, which refuses anything
  // without a token, so the load has to wait for the log in that mints one.
  // The guard is a ref rather than a dependency because RefIsLoggedIn's minute
  // interval rewrites the value from the log in timestamp to a boolean, which
  // would otherwise read as a second log in.
  const hasLoadedDatasets = useRef(false);

  useEffect(() => {
    if (!isLoggedIn || hasLoadedDatasets.current) return;

    hasLoadedDatasets.current = true;
    refreshAllDatasets();
  }, [isLoggedIn]);

  const logIn = async () => {
    const response = await fetch('/.netlify/functions/checkPassword', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });

    const { correct, token } = await response.json();

    if (!isNullOrUndefined(password) && correct) {
      setStoredValue(locCon.APP_TOKEN, token);

      // Minted before IS_LOGGED_IN, because setting that is what releases the
      // effect above to call refreshAllDatasets, and every one of those calls
      // needs this token. Not gated on success: a failure here surfaces as the
      // same per dataset error any other transport failure would.
      clearApiToken();
      await getApiToken();

      setStoredValue(locCon.IS_LOGGED_IN, formatISO(new Date()));
      setShowMessage(false);
    } else setShowMessage(true);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      logIn();
    }
  };

  // Uses a function through switch to render
  const renderDesktopComponents = (page) => {
    switch (page) {
      case locCon.PAGE_START:
        return <DashboardSelect />;
      case locCon.PAGE_DASHBOARD_EXPIRY_PARTICIPANTS:
        return <DashboardExpiryParticipants />;
      case locCon.PAGE_DASHBOARD_DEPLOYMENT_STATUS:
        return <DashboardDeploymentStatus />;
      case locCon.PAGE_DASHBOARD_COLLECTION_STATUS:
        return <DashboardCollectionStatus />;
      case locCon.PAGE_DASHBOARD_DEPLOYMENT_PROGRESS:
        return <DashboardDeploymentProgress />;
      case locCon.PAGE_DASHBOARD_DATA_STREAM_QUALITY:
        return <DashboardDataStreamQuality />;
      case locCon.PAGE_DASHBOARD_SINGLE_COLLECTION_STATUS:
        return <DashboardSingleCollectionStatus />;
      case locCon.PAGE_DASHBOARD_LAB_UPLOADS:
        return <DashboardUploadLabResults />;
      case locCon.PAGE_DASHBOARD_NAME_MATCHER:
        return <DashboardNameMatcher />;
      case locCon.PAGE_DASHBOARD_GLOBAL_COLLECTION_PROGRESS:
        return <DashboardGlobalCollectionStatus />;
      case locCon.PAGE_DASHBOARD_PRIORITY_VISITS:
        return <DashboardPriorityParticipantVisit />;
      case locCon.PAGE_DASHBOARD_PARTICIPANTS_TO_BE_UNENROLLED:
        return <DashboardParticipantsToBeUnenrolled />;
      case locCon.PAGE_DASHBOARD_GOVEE_CODES:
        return <DashboardGoveeActivationCodes />;
      case locCon.PAGE_DASHBOARD_ACTION_ITEMS:
        return <DashboardActionItems />;
      case locCon.PAGE_PARTICIPANT_UNENROLL:
        return <ParticipantUnenroll />;
      case locCon.PAGE_DASHBOARD_DOCUMENTS:
        return <DashboardDocuments />;
      case locCon.PAGE_DASHBOARD_EXHIBITION_DASHBOARDS_SELECT:
        return <DashboardExhibitionSelect />;
      case locCon.PAGE_DASHBOARD_EXHIBITION_FLIPPING_CARDS:
        return <DashboardExhibitionFlippingCards />;
      default:
        return <DashboardSelect />;
    }
  };

  return (
    <div>
      {/* <Button type='primary' onClick={() => updateAll()} >{"Update"}</Button> */}

      {locCon.DEBUG ? <p style={{ margin: ' 0px' }}>DEBUG IS ON</p> : <div></div>}
      {libCon.DEVELOPMENT ? <p style={{ margin: ' 0px' }}>Running on Development</p> : <div></div>}
      <div className="App">
        {/* No development or debug bypass: without a log in there is no token, and
            without a token the proxy refuses every back-end call. The exhibition
            pages stay open because they run entirely off local simulation data. */}
        {isLoggedIn || currentPage.includes(`${locCon.PAGE_DASHBOARD_EXHIBITION_DASHBOARDS_SELECT}/`) ? (
          <div className="verticalSection">
            {!currentPage.includes(`${locCon.PAGE_DASHBOARD_EXHIBITION_DASHBOARDS_SELECT}/`) ? (
              <Header showBackButton={currentPage !== locCon.PAGE_START} />
            ) : (
              <div></div>
            )}

            {renderDesktopComponents(currentPage)}
          </div>
        ) : (
          <div onKeyPress={handleKeyPress}>
            {' '}
            <Space direction="vertical">
              <Input.Password onChange={(e) => setPassword(e.target.value)} value={password} placeholder={i18n.t('password')} />
              <Button type="primary" onClick={() => logIn()}>
                {i18n.t('enter')}
              </Button>
              {showMessage ? <Text type="danger">{i18n.t('passwordIsNotCorrect')}</Text> : <div></div>}
            </Space>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
